// src/lib/dates.ts

/**
 * Returns today's date as a YYYY-MM-DD string in the São Paulo timezone (UTC-3).
 * Use this instead of new Date().toISOString().slice(0,10) which uses UTC/browser-local time.
 */
export function todaySaoPaulo(): string {
  return new Intl.DateTimeFormat("sv-SE", {
    timeZone: "America/Sao_Paulo",
  }).format(new Date());
}

/**
 * Subtracts the given number of days from a YYYY-MM-DD date string and returns
 * the result as YYYY-MM-DD. Works by parsing the date and adjusting the day.
 */
function subtractDays(dateStr: string, days: number): string {
  const [year = 0, month = 0, day = 0] = dateStr.split("-").map(Number);
  const d = new Date(Date.UTC(year, month - 1, day - days));
  return new Intl.DateTimeFormat("sv-SE", { timeZone: "UTC" }).format(d);
}

function currentWeekMonday(dateStr: string): string {
  const [year = 0, month = 0, day = 0] = dateStr.split("-").map(Number);
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  const daysSinceMonday = weekday === 0 ? 6 : weekday - 1;
  return subtractDays(dateStr, daysSinceMonday);
}

export interface DateRange {
  from: string;
  to: string;
}

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Checks a date-only value without applying the browser timezone. This validates
 * form/query boundaries only; the dashboard server remains responsible for
 * São Paulo period and comparison calculations.
 */
export function isValidISODate(value: string): boolean {
  const match = ISO_DATE_PATTERN.exec(value);
  if (!match) return false;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < 1 || month < 1 || month > 12 || day < 1) return false;

  const isLeapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [
    31,
    isLeapYear ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ];

  return day <= (daysInMonth[month - 1] ?? 0);
}

/** Equal bounds are valid because dashboard custom ranges are inclusive. */
export function isInclusiveISODateRange(from: string, to: string): boolean {
  return isValidISODate(from) && isValidISODate(to) && from <= to;
}

/**
 * Converts a TimeFilterBar period key into a from/to date range using
 * São Paulo local dates. Returns undefined when the period produces no valid range.
 *
 * Period semantics:
 *   today  → from = today (SP), to = today (SP)
 *   week   → from = Monday of the current calendar week (SP), to = today (SP)
 *   last7  → from = 6 days ago (SP), to = today (SP)
 *   month  → from = first calendar day of the current month (SP), to = today (SP)
 *   last30 → from = 29 days ago (SP), to = today (SP)
 *   year   → from = Jan 1 of current year (SP), to = today (SP)
 *   all    → from = 2020-01-01, to = today (SP)  [triggers degraded mode]
 *   custom → from = customFrom, to = customTo (pass-through)
 */
export function periodToSaoPauloRange(
  period: string,
  customFrom?: string,
  customTo?: string,
): DateRange | undefined {
  const today = todaySaoPaulo();

  switch (period) {
    case "today":
      return { from: today, to: today };

    case "week":
      return { from: currentWeekMonday(today), to: today };

    case "last7":
      return { from: subtractDays(today, 6), to: today };

    case "month":
      return { from: `${today.slice(0, 8)}01`, to: today };

    case "last30":
      return { from: subtractDays(today, 29), to: today };

    case "year": {
      const year = today.slice(0, 4);
      return { from: `${year}-01-01`, to: today };
    }

    case "all":
      return { from: "2020-01-01", to: today };

    case "custom":
      if (customFrom && customTo) return { from: customFrom, to: customTo };
      return undefined;

    default:
      return undefined;
  }
}
